/**
 * Protected Route Component
 * Handles role-based route protection
 */

import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Box, CircularProgress } from '@mui/material';

interface ProtectedRouteProps {
  allowedRoles?: string[];
  /** Set false ONLY on the /onboarding route itself. Every other protected
   *  route defaults to true: a pending identity (no tenant yet) hitting a
   *  tenant-scoped URL directly gets redirected to /onboarding instead of a
   *  dashboard page that would break on its first tenant-scoped fetch. If
   *  /onboarding used that same default, it would redirect to itself —
   *  React Router re-renders, the check fires again, still no tenant,
   *  navigates to /onboarding again — an infinite loop, seen live as a
   *  blank white screen after a successful pending-user login. */
  requireTenant?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ allowedRoles, requireTenant = true }) => {
  const { isAuthenticated, user, tenant, loading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking auth state
  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // If not authenticated, redirect to login — carrying the URL they were
  // actually trying to reach (path + query string, e.g. a deep link like
  // /admin/team?inviteEmail=...) so Login.tsx's existing `?redirect=`
  // handling (already used by the invite-accept flow) sends them there
  // after signing in, instead of always dropping them on the dashboard.
  // Without this, a logged-out visitor hitting any protected deep link lost
  // their destination the moment they had to log in first.
  if (!isAuthenticated) {
    const destination = `${location.pathname}${location.search}`;
    const isMeaningfulDestination = destination !== '/' && !destination.startsWith('/login');
    return (
      <Navigate
        to={isMeaningfulDestination ? `/login?redirect=${encodeURIComponent(destination)}` : '/login'}
        replace
      />
    );
  }

  // A pending identity (registered/invited but never finished creating or
  // joining a tenant) directly hitting a tenant-scoped dashboard URL — e.g. a
  // bookmarked link, a stale tab reopened after registering — must not reach
  // it; every dashboard page assumes tenant context and would just break on
  // its first fetch.
  const isTenantScopedRole = user?.role !== 'superadmin';
  if (requireTenant && isTenantScopedRole && !tenant) {
    return <Navigate to="/onboarding" replace />;
  }

  // Check role if specified
  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // User is authenticated and has required role
  return <Outlet />;
};

export default ProtectedRoute;
